/** Credit balance, packs, and checkout hooks */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, ApiError } from '../lib/api-client'

export type CreditBalance = {
  purchasedCredits: number
  freeCredits: number
  totalCredits: number
}

export type CreditPack = {
  id: string
  name: string
  credits: number
  bonusCredits: number
  priceCents: number
  sortOrder: number
}

export type CheckoutResponse = {
  checkoutUrl: string
}

export type PaymentsConfig = {
  environment: 'sandbox' | 'production'
}

export function usePaymentsConfig() {
  return useQuery<PaymentsConfig, ApiError>({
    queryKey: ['payments', 'config'],
    queryFn: () => apiClient.get<PaymentsConfig>('/api/payments/config'),
    staleTime: 1000 * 60 * 60,
  })
}

export function useCreditBalance() {
  return useQuery<CreditBalance, ApiError>({
    queryKey: ['credits', 'balance'],
    queryFn: () => apiClient.get<CreditBalance>('/api/credits/balance'),
    staleTime: 1000 * 30,
  })
}

export function useCreditPacks() {
  return useQuery<CreditPack[], ApiError>({
    queryKey: ['credits', 'packs'],
    queryFn: () => apiClient.get<CreditPack[]>('/api/credits/packs'),
    staleTime: 1000 * 60 * 10,
  })
}

export function useCreateCheckout() {
  const qc = useQueryClient()
  return useMutation<CheckoutResponse, ApiError, { packId: string }>({
    mutationFn: (input) => apiClient.post<CheckoutResponse>('/api/payments/checkout', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credits', 'balance'] })
    },
  })
}
